'use client';

import { c as _c } from "react/compiler-runtime";
import type { UIMessage as Message } from 'ai';
import { motion } from 'framer-motion';
import { CopyIcon } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useEventCallback } from 'usehooks-ts';
import type { MessagePart } from './message-part-utils';
import type { ToolRendererContext } from './message-tool-renderers';
import { handleMarkdownCopy } from './chat-markdown-copy';
import { ToolTraceGroup } from './message-blocks/tool-trace-group';
import { MemoizedDynamicToolPart, MemoizedStaticToolPart, MessageReasoningPart, MessageTextPart } from './message-memoized-parts';
import { buildTraceSummary, segmentParts } from './message-part-segments';
import { getMessagePartSignature, getMessageTextContent, getNextRenderablePart, getPersistentPartKey, getPreviousRenderablePart, getSafeExternalHref, getToolDisclosureKey, isAddPrCommentPart, isDynamicToolPart, isFilePart, isInteractiveProposalToolPart, isRenderedDisclosureBlockPart, isSourceUrlPart, isStepStartPart } from './message-part-utils';
import { applyAddPrComment, hasRequiredAddPrCommentInput } from './tools/apply-add-pr-comment';
import { retainPageActionReplays } from './tools/page-action-replays';
import { usePrCommentInput } from '@/components/inbox/prs/hooks/use-pr-comment-input';
import { Button } from '@/components/ui/button';
import { BetterTooltip } from '@/components/ui/tooltip';
import { useDiffNavigationStore } from '@/lib/stores/navigation/diff-navigation-store';
import { usePrStore } from '@/lib/stores/pr-store';
import { usePrTabNavigationStore } from '@/lib/stores/pr-tab-navigation/tab-navigation-store';
import { usePrV2DraftStore } from '@/lib/stores/pr-v2-draft-store';
import { cn } from '@/lib/utils';
import { copyToClipboard } from '@/utils/clipboard';
type ChatMessageRendererProps = {
  message: Message;
};
const NonMemoizedChatMessageRenderer = t0 => {
  const $ = _c(52);
  const {
    message
  } = t0;
  let t1;
  let t2;
  if ($[0] !== message.id) {
    t1 = () => retainPageActionReplays(message.id);
    t2 = [message.id];
    $[0] = message.id;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {};
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const [toolOpenState, setToolOpenState] = useState(t3);
  const {
    activeTab,
    switchToChangesTab
  } = usePrTabNavigationStore();
  const {
    togglePrCommentInput,
    activePrCommentInput
  } = usePrCommentInput();
  const {
    owner,
    repo,
    number
  } = usePrStore();
  const navigateToFile = useDiffNavigationStore(_temp);
  const getPrV2Draft = usePrV2DraftStore(_temp2);
  const setPrV2DraftForFile = usePrV2DraftStore(_temp3);
  let t4;
  if ($[4] !== message) {
    t4 = getMessageTextContent(message).trim();
    $[4] = message;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const messageMarkdown = t4;
  let t5;
  if ($[6] !== messageMarkdown) {
    t5 = () => {
      copyToClipboard(messageMarkdown, {
        successMessage: "Response copied as Markdown"
      });
    };
    $[6] = messageMarkdown;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const copyResponse = t5;
  const isEmptyAssistantMessage = message.role === "assistant" && (!message.parts || message.parts.length === 0);
  let t6;
  if ($[8] !== activePrCommentInput || $[9] !== activeTab || $[10] !== getPrV2Draft || $[11] !== message.parts || $[12] !== navigateToFile || $[13] !== number || $[14] !== owner || $[15] !== repo || $[16] !== setPrV2DraftForFile || $[17] !== switchToChangesTab || $[18] !== togglePrCommentInput) {
    t6 = toolCallId => {
      if (!toolCallId) {
        toast.error("No comment ID found");
        return;
      }
      const addPrCommentPart = message.parts?.find(part => part.type.startsWith("tool-") && isAddPrCommentPart(part) && part.toolCallId === toolCallId);
      if (!addPrCommentPart || !isAddPrCommentPart(addPrCommentPart)) {
        toast.error("No comment found to apply");
        return;
      }
      const args = addPrCommentPart.input;
      if (!args || !hasRequiredAddPrCommentInput(args)) {
        toast.error("Missing required comment data");
        return;
      }
      try {
        const result = applyAddPrComment({
          args,
          owner,
          repo,
          number,
          navigateToFile,
          getDraft: getPrV2Draft,
          setDraftForFile: setPrV2DraftForFile,
          togglePrCommentInput,
          activePrCommentInput,
          activeTab,
          switchToChangesTab
        });
        if (result === "applied") {
          toast.success("Comment draft opened");
        }
      } catch {
        toast.error("Failed to toggle comment");
      }
    };
    $[8] = activePrCommentInput;
    $[9] = activeTab;
    $[10] = getPrV2Draft;
    $[11] = message.parts;
    $[12] = navigateToFile;
    $[13] = number;
    $[14] = owner;
    $[15] = repo;
    $[16] = setPrV2DraftForFile;
    $[17] = switchToChangesTab;
    $[18] = togglePrCommentInput;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const handleAiPrComment = useEventCallback(t6);
  let t7;
  if ($[20] !== toolOpenState) {
    t7 = (part_0, index) => {
      const disclosureKey = getToolDisclosureKey(part_0, index);
      if (!disclosureKey) {
        return {
          open: false
        };
      }
      return {
        open: toolOpenState[disclosureKey] ?? isInteractiveProposalToolPart(part_0),
        onOpenChange: open => {
          setToolOpenState(current => {
            if (current[disclosureKey] === open) {
              return current;
            }
            return {
              ...current,
              [disclosureKey]: open
            };
          });
        }
      };
    };
    $[20] = toolOpenState;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const getToolProps = t7;
  if (isEmptyAssistantMessage) {
    return null;
  }
  let t8;
  if ($[22] !== getToolProps || $[23] !== handleAiPrComment || $[24] !== message.id || $[25] !== message.parts || $[26] !== message.role) {
    t8 = () => {
      const isReasoningStreaming = _temp4;
      const hasReasoningText = _temp5;
      const createsVisibleContent = part_3 => {
        if (isStepStartPart(part_3)) {
          return false;
        }
        if (part_3.type === "text") {
          return part_3.text.trim().length > 0;
        }
        if (part_3.type === "reasoning") {
          return hasReasoningText(part_3) || isReasoningStreaming(part_3);
        }
        if (isDynamicToolPart(part_3) || isSourceUrlPart(part_3) || isFilePart(part_3)) {
          return true;
        }
        if (part_3.type.startsWith("tool-")) {
          return true;
        }
        return false;
      };
      if (message.parts && message.parts.length > 0) {
        const hasTextPart = message.parts.some(_temp6);
        const segments = segmentParts(message.parts);
        const renderSinglePart = (part_5, index_0) => {
          const previousRenderablePart = getPreviousRenderablePart(message.parts, index_0);
          const nextRenderablePart = getNextRenderablePart(message.parts, index_0);
          const partSignature = getMessagePartSignature(part_5, index_0);
          const disclosureClassName = cn(isRenderedDisclosureBlockPart(previousRenderablePart) && "-mt-2", isRenderedDisclosureBlockPart(nextRenderablePart) ? "mb-1" : nextRenderablePart?.type === "text" ? "mb-0" : undefined);
          const toolProps = getToolProps(part_5, index_0);
          if (isDynamicToolPart(part_5)) {
            return <MemoizedDynamicToolPart key={getPersistentPartKey(message.id, part_5, index_0)} messageId={message.id} part={part_5} index={index_0} partSignature={partSignature} open={toolProps.open} onOpenChange={toolProps.onOpenChange} toolClassName={disclosureClassName} handleAiPrComment={handleAiPrComment} />;
          }
          if (part_5.type === "reasoning") {
            const reasoningIsStreaming = isReasoningStreaming(part_5);
            if (!hasReasoningText(part_5) && !reasoningIsStreaming) {
              return null;
            }
            return <MessageReasoningPart key={getPersistentPartKey(message.id, part_5, index_0)} messageId={message.id} index={index_0} className={disclosureClassName} text={part_5.text} defaultOpen={!hasTextPart} autoCollapse={hasTextPart} isStreaming={reasoningIsStreaming} />;
          }
          if (part_5.type === "text") {
            if (part_5.text.trim().length === 0) {
              return null;
            }
            return <MessageTextPart key={getPersistentPartKey(message.id, part_5, index_0)} messageId={message.id} index={index_0} text={part_5.text} />;
          }
          if (part_5.type.startsWith("tool-")) {
            return <MemoizedStaticToolPart key={getPersistentPartKey(message.id, part_5, index_0)} messageId={message.id} part={part_5} index={index_0} partSignature={partSignature} open={toolProps.open} onOpenChange={toolProps.onOpenChange} toolClassName={disclosureClassName} handleAiPrComment={handleAiPrComment} />;
          }
          if (isStepStartPart(part_5)) {
            const hasVisibleContentBefore = message.parts.slice(0, index_0).some(candidatePart => createsVisibleContent(candidatePart));
            const hasVisibleContentAfter = message.parts.slice(index_0 + 1).some(candidatePart_0 => createsVisibleContent(candidatePart_0));
            if (!hasVisibleContentBefore || !hasVisibleContentAfter) {
              return null;
            }
            if (isRenderedDisclosureBlockPart(previousRenderablePart) && isRenderedDisclosureBlockPart(nextRenderablePart)) {
              return null;
            }
            return <div key={getPersistentPartKey(message.id, part_5, index_0)} className="my-1" />;
          }
          if (isSourceUrlPart(part_5)) {
            const safeHref = getSafeExternalHref(part_5.url);
            if (!safeHref) {
              return <span key={getPersistentPartKey(message.id, part_5, index_0)} className="text-muted-foreground text-xs">{part_5.title ?? part_5.url}</span>;
            }
            return <a key={getPersistentPartKey(message.id, part_5, index_0)} href={safeHref} target="_blank" rel="noopener noreferrer" className="text-muted-foreground text-xs underline hover:text-foreground">{part_5.title ?? part_5.url}</a>;
          }
          if (part_5.type === "file" && isFilePart(part_5)) {
            const safeHref_0 = getSafeExternalHref(part_5.url);
            if (!safeHref_0) {
              return <span key={getPersistentPartKey(message.id, part_5, index_0)} className="text-muted-foreground inline-flex items-center gap-1 text-xs"><span>File:</span><span>{part_5.filename ?? part_5.mediaType}</span></span>;
            }
            return <a key={getPersistentPartKey(message.id, part_5, index_0)} href={safeHref_0} target="_blank" rel="noopener noreferrer" className="text-muted-foreground inline-flex items-center gap-1 text-xs underline hover:text-foreground"><span>File:</span><span>{part_5.filename ?? part_5.mediaType}</span></a>;
          }
          return null;
        };
        return <div className="flex min-w-0 flex-col gap-2">{segments.map(segment => {
            if (segment.kind === "single") {
              return renderSinglePart(message.parts[segment.index], segment.index);
            }
            const summary = buildTraceSummary(message.parts, segment.startIndex, segment.endIndex);
            return <ToolTraceGroup key={`trace-group:${message.id}:${segment.startIndex}-${segment.endIndex}`} summary={summary}>{Array.from({
                length: segment.endIndex - segment.startIndex + 1
              }, (_, offset) => {
                const partIndex = segment.startIndex + offset;
                const part_6 = message.parts[partIndex];
                return renderSinglePart(part_6, partIndex);
              })}</ToolTraceGroup>;
          })}</div>;
      }
      if (message.role === "assistant") {
        return <div className="flex flex-col gap-2 text-muted-foreground text-[13px] italic"><p>Assistant response received but no content to display.</p><p className="text-xs">Check console for debugging information.</p></div>;
      }
      return null;
    };
    $[22] = getToolProps;
    $[23] = handleAiPrComment;
    $[24] = message.id;
    $[25] = message.parts;
    $[26] = message.role;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  const renderMessageContent = t8;
  let t10;
  let t11;
  let t9;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      opacity: 0
    };
    t10 = {
      opacity: 1
    };
    t11 = {
      duration: 0.2,
      ease: "easeOut"
    };
    $[28] = t10;
    $[29] = t11;
    $[30] = t9;
  } else {
    t10 = $[28];
    t11 = $[29];
    t9 = $[30];
  }
  const t12 = message.role;
  let t13;
  if ($[31] !== message.role) {
    t13 = cn("flex min-w-0 w-full flex-col gap-4 rounded-md text-[13px]", message.role === "user" ? ["bg-muted border border-border text-zinc-300", "ml-auto w-fit max-w-[85%] px-2.5 py-2"] : "w-full");
    $[31] = message.role;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== message.role || $[34] !== messageMarkdown) {
    t14 = message.role === "assistant" ? event => handleMarkdownCopy(event, messageMarkdown) : undefined;
    $[33] = message.role;
    $[34] = messageMarkdown;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== renderMessageContent) {
    t15 = renderMessageContent();
    $[36] = renderMessageContent;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== t14 || $[39] !== t15) {
    t16 = <div className="flex min-w-0 w-full flex-col gap-2 overflow-x-hidden text-wrap wrap-break-word" onCopy={t14}>{t15}</div>;
    $[38] = t14;
    $[39] = t15;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== t13 || $[42] !== t16) {
    t17 = <div className={t13}>{t16}</div>;
    $[41] = t13;
    $[42] = t16;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  let t18;
  if ($[44] !== copyResponse || $[45] !== message.role || $[46] !== messageMarkdown) {
    t18 = message.role === "assistant" && messageMarkdown ? <BetterTooltip content="Copy Markdown" side="top"><Button type="button" variant="ghost" size="tiny" aria-label="Copy response as Markdown" className="absolute -bottom-5 left-0 text-muted-foreground opacity-0 group-hover/message:opacity-100 focus-visible:opacity-100" onClick={copyResponse}><CopyIcon aria-hidden={true} /></Button></BetterTooltip> : null;
    $[44] = copyResponse;
    $[45] = message.role;
    $[46] = messageMarkdown;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  let t19;
  if ($[48] !== message.role || $[49] !== t17 || $[50] !== t18) {
    t19 = <motion.div className="group/message relative min-w-0 w-full" initial={t9} animate={t10} transition={t11} data-role={t12}>{t17}{t18}</motion.div>;
    $[48] = message.role;
    $[49] = t17;
    $[50] = t18;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  return t19;
};
export const ChatMessageRenderer = NonMemoizedChatMessageRenderer;
function _temp(state) {
  return state.navigateToFile;
}
function _temp2(state_0) {
  return state_0.getDraft;
}
function _temp3(state_1) {
  return state_1.setDraftForFile;
}
function _temp4(part_1) {
  return part_1.type === "reasoning" && "state" in part_1 && typeof part_1.state === "string" && part_1.state === "streaming";
}
function _temp5(part_2) {
  return part_2.type === "reasoning" && part_2.text.trim().length > 0;
}
function _temp6(part_4) {
  return part_4.type === "text" && part_4.text.trim().length > 0;
}